/**
 * Tool palette + canvas actions for the abstract floorplan editor.
 * Left rail: select/pan tools, station type stamps, zone types, flow types.
 */
import { useTranslation } from 'react-i18next';
import { Box, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import type { IconType } from 'react-icons';
import {
  HiArrowUturnLeft,
  HiArrowUturnRight,
  HiCursorArrowRays,
  HiHandRaised as HiHandPan,
  HiTrash,
} from 'react-icons/hi2';
import { Tooltip } from '@themes/aegix';
import {
  FLOW_TYPE_OPTIONS,
  STATION_TYPE_OPTIONS,
  ZONE_TYPE_OPTIONS,
} from './constants';
import type { EditorTool } from './types';

interface ToolButtonProps {
  label: string;
  icon?: IconType;
  swatch?: string;
  active: boolean;
  onClick: () => void;
  e2e: string;
}

function ToolButton({
  label,
  icon: Icon,
  swatch,
  active,
  onClick,
  e2e,
}: ToolButtonProps) {
  return (
    <Tooltip content={label} positioning={{ placement: 'right' }}>
      <IconButton
        aria-label={label}
        size="sm"
        variant={active ? 'solid' : 'ghost'}
        colorPalette={active ? 'blue' : 'gray'}
        onClick={onClick}
        data-e2e={e2e}
      >
        {Icon ? (
          <Icon />
        ) : (
          <Box
            w={3}
            h={3}
            borderRadius="sm"
            bg={swatch}
            borderWidth="1px"
            borderColor="border"
          />
        )}
      </IconButton>
    </Tooltip>
  );
}

interface EditorToolbarProps {
  tool: EditorTool;
  canUndo: boolean;
  canRedo: boolean;
  onToolChange: (tool: EditorTool) => void;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
}

export function EditorToolbar({
  tool,
  canUndo,
  canRedo,
  onToolChange,
  onUndo,
  onRedo,
  onClear,
}: EditorToolbarProps) {
  const { t } = useTranslation();
  return (
    <VStack
      gap={3}
      p={2}
      borderRightWidth="1px"
      borderColor="border"
      overflowY="auto"
      align="stretch"
      flexShrink={0}
      data-e2e="floorplan-editor-toolbar"
    >
      <VStack gap={1}>
        <ToolButton
          label={t('reunification.toolSelectMove', 'Select / move')}
          icon={HiCursorArrowRays}
          active={tool.kind === 'select'}
          onClick={() => onToolChange({ kind: 'select' })}
          e2e="tool-select"
        />
        <ToolButton
          label={t('reunification.toolPanCanvas', 'Pan canvas')}
          icon={HiHandPan}
          active={tool.kind === 'pan'}
          onClick={() => onToolChange({ kind: 'pan' })}
          e2e="tool-pan"
        />
        <HStack gap={1}>
          <Tooltip content={t('common.undo', 'Undo')} positioning={{ placement: 'right' }}>
            <IconButton
              aria-label={t('common.undo', 'Undo')}
              size="xs"
              variant="ghost"
              disabled={!canUndo}
              onClick={onUndo}
              data-e2e="tool-undo"
            >
              <HiArrowUturnLeft />
            </IconButton>
          </Tooltip>
          <Tooltip content={t('common.redo', 'Redo')} positioning={{ placement: 'right' }}>
            <IconButton
              aria-label={t('common.redo', 'Redo')}
              size="xs"
              variant="ghost"
              disabled={!canRedo}
              onClick={onRedo}
              data-e2e="tool-redo"
            >
              <HiArrowUturnRight />
            </IconButton>
          </Tooltip>
        </HStack>
        <ToolButton
          label={t('reunification.toolClearCanvas', 'Clear canvas')}
          icon={HiTrash}
          active={false}
          onClick={onClear}
          e2e="tool-clear"
        />
      </VStack>

      <Box>
        <Text
          fontSize="2xs"
          fontWeight={700}
          color="text.secondary"
          textTransform="uppercase"
          px={1}
        >
          {t('reunification.stations', 'Stations')}
        </Text>
        <VStack gap={0.5} mt={1}>
          {STATION_TYPE_OPTIONS.map((option) => (
            <ToolButton
              key={option.value}
              label={t(`reunification.stationType.${option.value}`, option.label)}
              icon={option.icon}
              active={
                tool.kind === 'place-station' &&
                tool.stationType === option.value
              }
              onClick={() =>
                onToolChange({
                  kind: 'place-station',
                  stationType: option.value,
                })
              }
              e2e={`tool-station-${option.value}`}
            />
          ))}
        </VStack>
      </Box>

      <Box>
        <Text
          fontSize="2xs"
          fontWeight={700}
          color="text.secondary"
          textTransform="uppercase"
          px={1}
        >
          {t('reunification.zones', 'Zones')}
        </Text>
        <VStack gap={0.5} mt={1}>
          {ZONE_TYPE_OPTIONS.map((option) => (
            <ToolButton
              key={option.value}
              label={t('reunification.dragToDraw', '{{label}} (drag to draw)', {
                label: t(`reunification.zoneType.${option.value}`, option.label),
              })}
              swatch={option.defaultColor}
              active={
                tool.kind === 'draw-zone' && tool.zoneType === option.value
              }
              onClick={() =>
                onToolChange({ kind: 'draw-zone', zoneType: option.value })
              }
              e2e={`tool-zone-${option.value}`}
            />
          ))}
        </VStack>
      </Box>

      <Box>
        <Text
          fontSize="2xs"
          fontWeight={700}
          color="text.secondary"
          textTransform="uppercase"
          px={1}
        >
          {t('reunification.flows', 'Flows')}
        </Text>
        <VStack gap={0.5} mt={1}>
          {FLOW_TYPE_OPTIONS.map((option) => (
            <ToolButton
              key={option.value}
              label={t('reunification.clickSourceThenTarget', '{{label}} (click source, then target)', {
                label: t(`reunification.flowType.${option.value}`, option.label),
              })}
              swatch={option.color}
              active={
                tool.kind === 'draw-flow' && tool.flowType === option.value
              }
              onClick={() =>
                onToolChange({ kind: 'draw-flow', flowType: option.value })
              }
              e2e={`tool-flow-${option.value}`}
            />
          ))}
        </VStack>
      </Box>
    </VStack>
  );
}
